import React, { useId, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { API_PUBLIC_ROOT } from '../services/api';
import './ApiRequestPlayground.css';

const DEFAULT_TRACKING_ID = 'MSCU1234567';

const readBody = async (response) => {
  const text = await response.text();
  if (!text) return '';

  try {
    return JSON.stringify(JSON.parse(text), null, 2);
  } catch {
    return text;
  }
};

/**
 * A deliberately small, Postman-like request runner for testing a developer
 * key. The key remains only in React state for this page session: it is never
 * persisted to local storage, Firestore, or analytics.
 */
export default function ApiRequestPlayground() {
  const { isEmailVerified, openAuthModal } = useAuth();
  const keyId = useId();
  const trackingId = useId();
  const countryId = useId();

  const [apiKey, setApiKey] = useState('');
  const [trackingNumber, setTrackingNumber] = useState(DEFAULT_TRACKING_ID);
  const [destinationCountry, setDestinationCountry] = useState('');
  const [showKey, setShowKey] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  const sendRequest = async (event) => {
    event.preventDefault();
    setError('');
    setResult(null);

    const key = apiKey.trim();
    const id = trackingNumber.trim();
    if (!key || !id) {
      setError('Enter both an API key and a tracking reference.');
      return;
    }

    const body = {
      trackingId: id,
      ...(destinationCountry.trim() && { destinationCountry: destinationCountry.trim() }),
    };

    setIsSending(true);
    const startedAt = performance.now();
    try {
      const response = await fetch(`${API_PUBLIC_ROOT}/v1/track`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': key,
        },
        body: JSON.stringify(body),
      });

      const durationMs = Math.round(performance.now() - startedAt);
      const headers = ['x-ratelimit-remaining', 'x-quota-remaining', 'retry-after']
        .map((name) => [name, response.headers.get(name)])
        .filter(([, value]) => value !== null);

      setResult({
        ok: response.ok,
        status: response.status,
        durationMs,
        headers,
        body: await readBody(response),
      });
    } catch {
      setError('The request could not reach the API. Check that the API is running and this site is allowed by CORS.');
    } finally {
      setIsSending(false);
    }
  };

  if (!isEmailVerified) {
    return (
      <section className="api-playground api-playground-locked" aria-labelledby="try-api-heading">
        <p className="api-playground-kicker">Interactive API tester</p>
        <h2 id="try-api-heading">Try a request</h2>
        <p>Verify your email first, then create a key to send a live test request.</p>
        <button className="api-playground-button" onClick={openAuthModal}>Verify email</button>
      </section>
    );
  }

  return (
    <section id="try-api" className="api-playground" aria-labelledby="try-api-heading">
      <div className="api-playground-heading">
        <div>
          <p className="api-playground-kicker">Interactive API tester</p>
          <h2 id="try-api-heading">Try a request</h2>
          <p>Send a live request with your key and inspect the exact response below.</p>
        </div>
        <span className="api-playground-method">POST</span>
      </div>

      <p className="api-playground-endpoint" aria-label={`Endpoint ${API_PUBLIC_ROOT}/v1/track`}>
        <code>{API_PUBLIC_ROOT}/v1/track</code>
      </p>

      <form className="api-playground-form" onSubmit={sendRequest}>
        <label className="api-playground-field" htmlFor={keyId}>
          <span>API key</span>
          <div className="api-playground-key-input">
            <input
              id={keyId}
              type={showKey ? 'text' : 'password'}
              value={apiKey}
              onChange={(event) => setApiKey(event.target.value)}
              placeholder="rl_live_..."
              autoComplete="off"
              spellCheck="false"
              required
            />
            <button
              type="button"
              className="api-playground-text-button"
              onClick={() => setShowKey((visible) => !visible)}
              aria-pressed={showKey}
            >
              {showKey ? 'Hide' : 'Show'}
            </button>
          </div>
        </label>

        <div className="api-playground-fields-row">
          <label className="api-playground-field" htmlFor={trackingId}>
            <span>Tracking reference</span>
            <input
              id={trackingId}
              value={trackingNumber}
              onChange={(event) => setTrackingNumber(event.target.value)}
              placeholder="MSCU1234567"
              required
            />
          </label>
          <label className="api-playground-field" htmlFor={countryId}>
            <span>Destination country <em>optional</em></span>
            <input
              id={countryId}
              value={destinationCountry}
              onChange={(event) => setDestinationCountry(event.target.value)}
              placeholder="Uganda"
            />
          </label>
        </div>

        <div className="api-playground-actions">
          <button className="api-playground-button" type="submit" disabled={isSending}>
            {isSending ? 'Sending request…' : 'Send request'}
          </button>
          <p>Your key is used only for this request and is not saved in this browser.</p>
        </div>
      </form>

      <div className="api-playground-result" aria-live="polite" aria-busy={isSending}>
        <div className="api-playground-result-head">
          <h3>Response</h3>
          {result && (
            <div className="api-playground-result-meta">
              <span className={result.ok ? 'api-playground-status success' : 'api-playground-status error'}>
                {result.status}
              </span>
              <span>{result.durationMs} ms</span>
            </div>
          )}
        </div>
        {isSending && <p className="api-playground-pending">Calling the tracking API…</p>}
        {error && <p className="api-playground-error" role="alert">{error}</p>}
        {!isSending && !error && !result && <p className="api-playground-empty">Your formatted JSON response will appear here.</p>}
        {result && (
          <>
            {result.headers.length > 0 && (
              <ul className="api-playground-headers" aria-label="Response headers">
                {result.headers.map(([name, value]) => <li key={name}><code>{name}</code>: {value}</li>)}
              </ul>
            )}
            <pre className="api-playground-json"><code>{result.body || 'No response body returned.'}</code></pre>
          </>
        )}
      </div>
    </section>
  );
}
